import { useMutation } from '@tanstack/react-query';
import {
  CANCEL_REASON_LABELS,
  CANCEL_REASONS,
  type CancelInvoiceRequest,
  type CancelReason,
  formatMoney,
  type InvoiceResponse,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';

/**
 * «Annulla la fattura» (2.15): on a cancelled invoice the box that says when,
 * why and by what replaced, with «Ripristina la fattura»; on the others, for
 * the CFO, the command. Gestify is not touched.
 */
export function InvoiceCancellation({
  invoice,
  onSaved,
}: {
  invoice: InvoiceResponse;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const canWrite = useCan('tesoreria.write');
  const mayClose = useCan('tesoreria.closures');
  const allowed = canWrite && mayClose;
  const [asking, setAsking] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restore = useMutation({
    mutationFn: () =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/cancel`, { method: 'DELETE' }),
    onSuccess: async (updated) => {
      setRestoring(false);
      await onSaved(updated);
    },
  });
  const cancellation = invoice.cancellation;
  if (cancellation) {
    return (
      <div className="notice stack tight">
        <span>
          <strong>Annullata</strong> il {formatTimestamp(cancellation.at)}
          {cancellation.by && ` da ${cancellation.by}`} ·{' '}
          {CANCEL_REASON_LABELS[cancellation.reason]}
          {cancellation.note && <> · «{cancellation.note}»</>}
          {cancellation.replacedBy && (
            <>
              {' '}
              · sostituita dalla{' '}
              <Link to={`/tesoreria/fatture/${cancellation.replacedBy.id}`}>
                {cancellation.replacedBy.number} del {formatDate(cancellation.replacedBy.date)}
              </Link>
            </>
          )}
        </span>
        <span className="muted small">
          Non conta nei totali, nello scadenzario e nelle proposte. In Gestify non è cambiato nulla.
        </span>
        {allowed && (
          <span>
            <button type="button" className="small" onClick={() => setRestoring(true)}>
              Ripristina la fattura
            </button>
          </span>
        )}
        {restoring && (
          <ConfirmDialog
            title={`Ripristina la fattura ${invoice.number}`}
            confirmLabel="Ripristina"
            busy={restore.isPending}
            error={restore.error?.message ?? null}
            onConfirm={() => restore.mutate()}
            onCancel={() => setRestoring(false)}
          >
            <p>
              La fattura torna a contare nei totali e nello scadenzario. Un anticipo passato alla
              fattura che la sostituiva resta lì: si sposta da Anticipi fatture.
            </p>
          </ConfirmDialog>
        )}
      </div>
    );
  }
  if (!allowed || invoice.kind === 'proforma' || invoice.origin === 'apertura') return null;
  return (
    <>
      <p className="muted small">
        Fattura che non vale (scartata dallo SDI o emessa per errore)?{' '}
        <button type="button" className="link small" onClick={() => setAsking(true)}>
          Annulla la fattura…
        </button>
      </p>
      {asking && (
        <CancelDialog
          invoice={invoice}
          onClose={() => setAsking(false)}
          onSaved={async (updated) => {
            setAsking(false);
            await onSaved(updated);
          }}
        />
      )}
    </>
  );
}

function CancelDialog({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: InvoiceResponse;
  onClose: () => void;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const [reason, setReason] = useState<CancelReason>('scartata_sdi');
  const [note, setNote] = useState('');
  const [replacedBy, setReplacedBy] = useState<string>(
    invoice.cancelReplacements.find((option) => option.sameTotal)?.id ?? '',
  );
  const cancel = useMutation({
    mutationFn: () => {
      const body: CancelInvoiceRequest = { reason, note, replacedBy: replacedBy || null };
      return apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/cancel`, {
        method: 'POST',
        body,
      });
    },
    onSuccess: onSaved,
  });
  const replacement = invoice.cancelReplacements.find((option) => option.id === replacedBy);
  const settled = invoice.settlements.length > 0;
  return (
    <ConfirmDialog
      title={`Annulla la fattura ${invoice.number} del ${formatDate(invoice.date)}`}
      confirmLabel="Annulla la fattura"
      busy={cancel.isPending}
      disabled={settled || note.trim().length < 3}
      cancelLabel="Lascia com’è"
      error={cancel.error?.message ?? null}
      onConfirm={() => cancel.mutate()}
      onCancel={onClose}
    >
      <div className="stack tight">
        <p className="muted">
          Per una fattura che non vale: scartata dallo SDI o emessa per errore e riemessa. Non è un
          incasso né uno stralcio: esce dai totali, dallo scadenzario e dalle proposte. In Gestify
          non cambia nulla.
        </p>
        {settled && (
          <p className="notice">
            La fattura ha incassi, pagamenti o collegamenti: toglili prima di annullarla.
          </p>
        )}
        <Field label="Motivo">
          <select
            value={reason}
            onChange={(event) => setReason(event.target.value as CancelReason)}
          >
            {CANCEL_REASONS.map((item) => (
              <option key={item} value={item}>
                {CANCEL_REASON_LABELS[item]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Sostituita dalla fattura">
          <select value={replacedBy} onChange={(event) => setReplacedBy(event.target.value)}>
            <option value="">— nessuna</option>
            {invoice.cancelReplacements.map((option) => (
              <option key={option.id} value={option.id}>
                {option.number} del {formatDate(option.date)} · {formatMoney(option.total)}
                {option.sameTotal ? ' (stesso importo)' : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Note">
          <textarea
            rows={2}
            maxLength={500}
            value={note}
            placeholder="Es. scartata, riemessa come FT 29"
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
        {invoice.advances.length > 0 && (
          <p className="notice info small">
            {invoice.advances.length === 1 ? 'L’anticipo della banca' : 'Gli anticipi della banca'}{' '}
            su questa fattura{' '}
            {replacement
              ? `passa${invoice.advances.length === 1 ? '' : 'no'} alla ${replacement.number}, che poi si chiude con «Incassata dalla banca».`
              : 'restano qui: indica la fattura che la sostituisce per spostarli.'}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
